import {computed, inject, onBeforeMount, provide, reactive, ref} from 'vue';
import AuthLoginComponent from '@/components/auth/blocks/login/login.vue';
import EventReplaySessionsColumn from '@/components/events/blocks/event-replay-sessions-column/event-replay-sessions-column.vue';
export default {
  name: 'EventReplaySectionsView',
  props: {
    config: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: false,
      default: null
    },
    layoutConfig: {
      type: Array,
      required: true,
    },
    event: {
      type: [Number, String],
      required: true,
    },
    showDownloads: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: { AuthLoginComponent, EventReplaySessionsColumn },
  setup(props) {

    const config = { ...inject('config'), ...props.config };
    provide('config', config);

    const eventProvider = inject('eventProvider');
    const authProvider = inject('authProvider');

    const state = reactive({
      notification: ref('Initializing Replay...'),
      showLogin: ref(false),
      hasAccess: ref(false),
      defaultTabColor: '#31373E',
      hasUser: computed(() => authProvider().hasUser),
      layoutData: [],
      playlist: ref(null),
      jwPlayer: {player: null},
      showPlayer: false,
      event: null,
    });

    onBeforeMount(async () => {
      await loadJWScript();
    });

    const loadJWScript = () => {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          let script = document.createElement('script')
          script.async = true
          script.src = 'https://cdn.jwplayer.com/libraries/RwDKnRJN.js?v=4.0.2'
          document.head.appendChild(script)
          resolve('jwloaded');
        }, 1000);
      })
    }

    const initialize = async () => {
        if (props.user) {
          await loadUser(props.user);
        } else if ( authProvider().hasUserCookie ) {
          await loadUser(authProvider().getUserCookie);
        } else {
          const cookie = authProvider().loadUserCookie(config);
          if (cookie) {
            await loadUser(cookie);
          }
        }
        if (state.hasUser) {
          await loadEvent();
        } else {
          state.notification = 'Please login with the account used to purchase this event.';
          state.showLogin = true;
        }

    };

    const sendUserActivity = async (videoToken) => {
      const user = authProvider().getUser;
      let data = {};
      data.user_id = user.id;
      data.event_id = props.event;
      data.url = window.location.pathname;
      data.video_key = videoToken;
      let response = await authProvider().sendUserActivity(user, data, config);

      return response;

    };

    const loadUser = async (user) => {
      state.notification = 'Loading User...';

      const { id, token } = user;
      let response = await authProvider().loadUser(id, token, config);
      await handleLoginResponse(response);
    };

    const login = async (data) => {
      state.notification = 'Validating Credentials...';

      const { username, password } = data;
      const response = await authProvider().login(username, password, config);
      await handleLoginResponse(response);
    };

    const handleLoginResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showLogin = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showLogin = false;
        state.notification = 'Verifying User Access';
        document.body.classList.remove('koc-has-overlay');
        await loadEvent();
      }
    };

    const filterSessionReplay = (session) => {
      return session.video_key;
    }

    const getTrack = (tracks, id) => tracks.find(track => track.id == id) || false;

    const filterSessionByStartDay = (startDay) => {
      return function(session) {
        let date = session.starts_at;
        let dayValue = date.slice(8, 10);

        return parseInt(dayValue, 10) === parseInt(startDay, 10);
      };
    };

    const sortSessionsByDate = (a,b) => {
      const startA = new Date(a.starts_at);
      const startB = new Date(b.starts_at);
      return startA > startB ? 1 : startA < startB ? -1 : 0;
    }

    const loadEvent = async () => {
      let response = await eventProvider().loadEvent(props.event, config, authProvider().getUser.token);
      if (response.status === 'error' || response.success === false) {
        state.notification = 'There was an error loading the event data.';
        if (config.debug) {
          console.log('loadEvent error', props.event, response.message);
        }
      } else {
        await handleEventResponse(response);
      }

      return response;
    };

    const handleEventResponse = async (response) => {
      state.event = response.data.data;
      state.layoutData = props.layoutConfig || (state.event?.schedule?.replay || []);
      let firstTrack = state.event.tracks[0];
      if (!firstTrack) {
        state.notification = `The track ID could not be located inside this event`;
        return;
      }
      if (!firstTrack.video_key) {
        state.notification = `This account does not have access to the ${state.event.name}<br/> <a href="${state.event.checkout_url}">PURCHASE THE EVENT REPLAY</a>`;
        return;
      }
      await organizeSectionData();
      state.playlist = firstTrack.video_key;
      state.notification = null;
      state.hasAccess = true;
    };

    const getDaySessions = (day) => {
      const sessions = [];
      if (state.event?.tracks?.length) {
        for (const track of state.event.tracks) {
          const filtered = track.sessions.filter(filterSessionByStartDay(day));
          if (filtered.length) {
            sessions.push(...filtered);
          }
        }
      }

      return sessions;
    }
    const organizeSectionData = async () => {
      for (let i = 0; i < state.layoutData.length; i++) {
        if (state.layoutData[i].rows.length) {
          if(state.layoutData[i].dayColumns) {
            for (let k = 0; k < state.layoutData[i].rows.length; k++) {
              for (let m = 0; m < state.layoutData[i].rows[k].length; m++) {
                const day = state.layoutData[i].rows[k][m].day;
                if (day) {
                  state.layoutData[i].rows[k][m].sessions = [];
                  const sessions = getDaySessions(day);
                  if(sessions.length) {
                    state.layoutData[i].rows[k][m].sessions = sessions.filter(filterSessionReplay).sort(sortSessionsByDate);
                  }
                }
              }
            }
          }
          else {
            for (let k = 0; k < state.layoutData[i].rows.length; k++) {
              for (let m = 0; m < state.layoutData[i].rows[k].length; m++) {
                let trackID = state.layoutData[i].rows[k][m].track;
                if (trackID) {
                  let trackData = getTrack(state.event.tracks, trackID);
                  if (trackData) {
                    state.layoutData[i].rows[k][m].sessions = [];
                    let filteredReplay = trackData.sessions.filter(filterSessionReplay);
                    if (filteredReplay.length) {
                      let sorted = filteredReplay.sort(sortSessionsByDate);
                      if (state.layoutData[i].rows[k][m].day) {
                        state.layoutData[i].rows[k][m].sessions = sorted.filter(filterSessionByStartDay(state.layoutData[i].rows[k][m].day));
                      } else {
                        state.layoutData[i].rows[k][m].sessions = sorted;
                      }
                    }
                    state.layoutData[i].rows[k][m].track_color = trackData.custom_class ? trackData.custom_class : state.defaultTabColor;
                  }
                }
              }
            }
          }
        }
      }

      state.showReplay = true;
      return;
    };

    initialize();

    document.addEventListener('koc-login', async (event) => {
      if (state.hasUser && event.detail.user.id !== state.user.id) {
        await authProvider().logout(config);
        state.hasAccess = false;
      }
      return await loadUser(event.detail.user);
    });

    document.addEventListener('koc-logout', async () => {
      if (state.hasUser) {
        state.hasAccess = false;
        state.showLogin = true;
        return await authProvider().logout(config);
      }
    });

    return {
      state,
      loadEvent,
      loadUser,
      login,
      sendUserActivity,
    };
  },
  methods: {
    playVideo: async function(token) {
      const videoUrl = '//cdn.jwplayer.com/v2/media/' + token;
      jwplayer('jwplaylist-container').setup({
        'playlist': videoUrl,
      }).setConfig({'autostart': true});
      this.state.showPlayer = true;
      const player = document.getElementById('jwplaylist-container');
      window.scroll(0, this.findPos(player));
      await this.sendUserActivity(token);
    },
    findPos: function(obj) {
      let curtop = 0;
      if (obj.offsetParent) {
        do {
          curtop += obj.offsetTop - 120;
        } while (obj === obj.offsetParent);
        return [curtop];
      }
      return [curtop];
    }
  },
};
